import { motion, AnimatePresence } from 'framer-motion'
import { useFloatingActionIsland } from '@/contexts/FloatingActionIslandContext'
import { cn } from '@/lib/utils'

interface FloatingActionIslandProps {
  className?: string
  position?: 'bottom' | 'top'
}

export function FloatingActionIsland({
  className,
  position = 'bottom',
}: FloatingActionIslandProps) {
  const { currentActions, isVisible } = useFloatingActionIsland()

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          key="floating-action-island"
          initial={{
            opacity: 0,
            y: position === 'bottom' ? 100 : -100,
            scale: 0.95,
            filter: 'blur(10px)',
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
          }}
          exit={{
            opacity: 0,
            y: position === 'bottom' ? 100 : -100,
            scale: 0.95,
            filter: 'blur(8px)',
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30,
            mass: 0.8,
          }}
          className={cn(
            'absolute z-50 left-1/2 -translate-x-1/2',
            'backdrop-blur-lg backdrop-saturate-150',
            'border border-gray-200/60 dark:border-white/10 rounded-2xl',
            'shadow-xl shadow-black/10',
            'px-4 py-2',
            'max-w-4xl',
            'min-w-fit',
            'pointer-events-none',
            position === 'bottom' ? 'bottom-6' : 'top-6',
            className,
          )}
        >
          <div
            className="absolute inset-0 rounded-2xl dark:bg-card/90"
            style={{ backgroundColor: 'oklch(0.985 0 0 / 0.05)' }}
          />

          <div className="relative z-10">
            {currentActions.map((action, index) => (
              <motion.div
                key={action.id}
                initial={{
                  opacity: 0,
                  x: -20,
                  filter: 'blur(4px)',
                  scale: 0.98,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                  filter: 'blur(0px)',
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  x: 20,
                  filter: 'blur(4px)',
                  scale: 0.98,
                }}
                transition={{
                  delay: index * 0.1 + 0.15, // Slight delay after container animates in
                  type: 'spring',
                  stiffness: 500,
                  damping: 35,
                  duration: 0.6,
                }}
                className={cn(
                  'flex items-center',
                  index > 0 &&
                    'mt-3 pt-3 border-t border-gray-100 dark:border-border',
                )}
              >
                {action.content}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
